import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react'
import { RhyBusLocalStorage } from '@rhybus/browser-storage'
import {
  getAnnouncementItems,
  getCalendarEventItems,
  getKnowledgeGraphItems,
  getQaItems,
  getPOCItems,
  getQuickLinkItems,
  type AnnouncementItem,
  type CalendarEventItem,
  type KnowledgeGraphItem,
  type QaItem,
  type PointOfContactItem,
  type QuickLinkItem,
} from '../../services/contentManagement'
import {
  getToolTipItems,
  getMetadataItemsByType,
  getChecklistItems,
  type TooltipMetadataItem,
  type CommonMetadataItem,
  type ChecklistMetadataItem,
} from '../../services/metadataManagement'
import { getReleaseNotesItems, type ReleaseNoteItem } from '../../services/releaseNotes'
import { getSiteFeedbackItems } from '../../services/siteFeedback'

const TTL_MS = 5 * 60 * 1000 // 5 minutes

type CacheEntry<T> = { data: T; timestamp: number }

function isFresh<T>(entry: CacheEntry<T> | null): entry is CacheEntry<T> {
  if (!entry?.data) return false
  if (Array.isArray(entry.data) && (entry.data as unknown[]).length === 0) return false
  return (Date.now() - entry.timestamp) < TTL_MS
}

// Derive a site-scoped prefix so dev and int sites never share localStorage keys
const _siteSegment = window.location.href.split('/')[5] ?? window.location.hostname
const _k = (name: string) => `rhybus_settings_${name}_${_siteSegment}`

const CACHE_KEYS = {
  announcements:   _k('announcements'),
  calendar:        _k('calendar'),
  knowledgegraphs: _k('knowledgegraphs'),
  qanda:           _k('qanda'),
  tooltips:        _k('tooltips'),
  releaseNotes:    _k('releasenotes'),
  sitefeedback:    _k('sitefeedback'),
  poc:             _k('poc'),
  quickLinks:      _k('quicklinks'),
  country:         _k('country'),
  exa:             _k('exa'),
  facilityCategory: _k('facilitycategory'),
  implementingAgency: _k('implementingagency'),
  location:        _k('location'),
  priority:        _k('priority'),
  programType:     _k('programtype'),
  rank:            _k('rank'),
  trainingType:    _k('trainingtype'),
  branch:          _k('branch'),
  division:        _k('division'),
  product:         _k('product'),
  tbm:             _k('tbm'),
  buildVersion:    _k('buildversion'),
  checklist:       _k('checklist'),
} as const

function makeQueryFn<T>(cacheKey: string, fetcher: () => Promise<T>) {
  return async () => {
    try {
      let cached: CacheEntry<T> | null = null
      try {
        cached = RhyBusLocalStorage.getItem(cacheKey) as CacheEntry<T> | null
      } catch {
        try { RhyBusLocalStorage.removeItem(cacheKey) } catch { /* ignore */ }
      }
      if (isFresh(cached)) return { data: cached.data }
      const data = await fetcher()
      try { RhyBusLocalStorage.setItem(cacheKey, { data, timestamp: Date.now() }) } catch { /* ignore */ }
      return { data }
    } catch (error) {
      return { error: { status: 'CUSTOM_ERROR', error: String(error) } }
    }
  }
}

function makeFreshQueryFn<T>(cacheKey: string, fetcher: () => Promise<T>) {
  return async () => {
    try {
      const data = await fetcher()
      RhyBusLocalStorage.setItem(cacheKey, { data, timestamp: Date.now() })
      return { data }
    } catch (error) {
      return { error: { status: 'CUSTOM_ERROR', error: String(error) } }
    }
  }
}

function makeRefreshFn(cacheKey: string) {
  return () => {
    RhyBusLocalStorage.removeItem(cacheKey)
    return { data: undefined as void }
  }
}

// Kept for any consumers that still reference this aggregate type
export type MappedSettingsData = {
  announcements: AnnouncementItem[]
  calendar: CalendarEventItem[]
  knowledgegraphs: KnowledgeGraphItem[]
  qanda: QaItem[]
  tooltips: TooltipMetadataItem[]
  releaseNotes: ReleaseNoteItem[]
  sitefeedback: any[]
  pointsOfContact: PointOfContactItem[]
  quickLinks: QuickLinkItem[]
}

export const settingsApi = createApi({
  reducerPath: 'settingsApi',
  baseQuery: fakeBaseQuery(),
  tagTypes: [
    'AnnouncementList', 'Calendar', 'KnowledgeGraphs', 'QandA',
    'Tooltips', 'ReleaseNotes', 'SiteFeedback', 'POC', 'QuickLinks',
    'Country', 'EXA', 'FacilityCategory', 'ImplementingAgency', 'Location',
    'Priority', 'ProgramType', 'Rank', 'TrainingType',
    'Branch', 'Division', 'Product', 'TBM', 'BuildVersion', 'Checklist',
  ],
  endpoints: (builder) => ({
    // ── queries ────────────────────────────────────────────────────────────────
    getAnnouncements: builder.query<AnnouncementItem[], void>({
      providesTags: ['AnnouncementList'],
      queryFn: makeFreshQueryFn(CACHE_KEYS.announcements, getAnnouncementItems),
    }),
    getCalendar: builder.query<CalendarEventItem[], void>({
      providesTags: ['Calendar'],
      queryFn: makeQueryFn(CACHE_KEYS.calendar, getCalendarEventItems),
    }),
    getKnowledgeGraphs: builder.query<KnowledgeGraphItem[], void>({
      providesTags: ['KnowledgeGraphs'],
      queryFn: makeQueryFn(CACHE_KEYS.knowledgegraphs, getKnowledgeGraphItems),
    }),
    getQandA: builder.query<QaItem[], void>({
      providesTags: ['QandA'],
      queryFn: makeQueryFn(CACHE_KEYS.qanda, getQaItems),
    }),
    getTooltips: builder.query<TooltipMetadataItem[], void>({
      providesTags: ['Tooltips'],
      queryFn: makeQueryFn(CACHE_KEYS.tooltips, getToolTipItems),
    }),
    getReleaseNotes: builder.query<ReleaseNoteItem[], void>({
      providesTags: ['ReleaseNotes'],
      queryFn: makeQueryFn(CACHE_KEYS.releaseNotes, getReleaseNotesItems),
    }),
    getSiteFeedback: builder.query<any[], void>({
      providesTags: ['SiteFeedback'],
      queryFn: makeQueryFn(CACHE_KEYS.sitefeedback, getSiteFeedbackItems),
    }),
    getPOC: builder.query<PointOfContactItem[], void>({
      providesTags: ['POC'],
      queryFn: makeQueryFn(CACHE_KEYS.poc, getPOCItems),
    }),
    getQuickLinks: builder.query<QuickLinkItem[], void>({
      providesTags: ['QuickLinks'],
      queryFn: makeQueryFn(CACHE_KEYS.quickLinks, getQuickLinkItems),
    }),
    getCountry: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Country'],
      queryFn: makeQueryFn(CACHE_KEYS.country, () => getMetadataItemsByType('Country')),
    }),
    getExa: builder.query<CommonMetadataItem[], void>({
      providesTags: ['EXA'],
      queryFn: makeQueryFn(CACHE_KEYS.exa, () => getMetadataItemsByType('EXA')),
    }),
    getFacilityCategory: builder.query<CommonMetadataItem[], void>({
      providesTags: ['FacilityCategory'],
      queryFn: makeQueryFn(CACHE_KEYS.facilityCategory, () => getMetadataItemsByType('FacilityCategory')),
    }),
    getImplementingAgency: builder.query<CommonMetadataItem[], void>({
      providesTags: ['ImplementingAgency'],
      queryFn: makeQueryFn(CACHE_KEYS.implementingAgency, () => getMetadataItemsByType('ImplementingAgency')),
    }),
    getLocation: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Location'],
      queryFn: makeQueryFn(CACHE_KEYS.location, () => getMetadataItemsByType('Location')),
    }),
    getPriority: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Priority'],
      queryFn: makeQueryFn(CACHE_KEYS.priority, () => getMetadataItemsByType('Priority')),
    }),
    getProgramType: builder.query<CommonMetadataItem[], void>({
      providesTags: ['ProgramType'],
      queryFn: makeQueryFn(CACHE_KEYS.programType, () => getMetadataItemsByType('ProgramType')),
    }),
    getRank: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Rank'],
      queryFn: makeQueryFn(CACHE_KEYS.rank, () => getMetadataItemsByType('Rank')),
    }),
    getTrainingType: builder.query<CommonMetadataItem[], void>({
      providesTags: ['TrainingType'],
      queryFn: makeQueryFn(CACHE_KEYS.trainingType, () => getMetadataItemsByType('TrackType')),
    }),
    getBranch: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Branch'],
      queryFn: makeQueryFn(CACHE_KEYS.branch, () => getMetadataItemsByType('Branch')),
    }),
    getDivision: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Division'],
      queryFn: makeQueryFn(CACHE_KEYS.division, () => getMetadataItemsByType('Division')),
    }),
    getProduct: builder.query<CommonMetadataItem[], void>({
      providesTags: ['Product'],
      queryFn: makeQueryFn(CACHE_KEYS.product, () => getMetadataItemsByType('Product')),
    }),
    getTbm: builder.query<CommonMetadataItem[], void>({
      providesTags: ['TBM'],
      queryFn: makeQueryFn(CACHE_KEYS.tbm, () => getMetadataItemsByType('TBM')),
    }),
    getBuildVersion: builder.query<CommonMetadataItem[], void>({
      providesTags: ['BuildVersion'],
      queryFn: makeQueryFn(CACHE_KEYS.buildVersion, () => getMetadataItemsByType('BuildVersion')),
    }),
    getChecklist: builder.query<ChecklistMetadataItem[], void>({
      providesTags: ['Checklist'],
      queryFn: makeQueryFn(CACHE_KEYS.checklist, () => getChecklistItems()),
    }),

    // ── per-section refresh mutations ──────────────────────────────────────────
    refreshAnnouncements: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.announcements),
      invalidatesTags: ['AnnouncementList'],
    }),
    refreshCalendar: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.calendar),
      invalidatesTags: ['Calendar'],
    }),
    refreshKnowledgeGraphs: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.knowledgegraphs),
      invalidatesTags: ['KnowledgeGraphs'],
    }),
    refreshQandA: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.qanda),
      invalidatesTags: ['QandA'],
    }),
    refreshTooltips: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.tooltips),
      invalidatesTags: ['Tooltips'],
    }),
    refreshReleaseNotes: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.releaseNotes),
      invalidatesTags: ['ReleaseNotes'],
    }),
    refreshSiteFeedback: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.sitefeedback),
      invalidatesTags: ['SiteFeedback'],
    }),
    refreshPOC: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.poc),
      invalidatesTags: ['POC'],
    }),
    refreshQuickLinks: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.quickLinks),
      invalidatesTags: ['QuickLinks'],
    }),
    refreshCountry: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.country),
      invalidatesTags: ['Country'],
    }),
    refreshExa: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.exa),
      invalidatesTags: ['EXA'],
    }),
    refreshFacilityCategory: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.facilityCategory),
      invalidatesTags: ['FacilityCategory'],
    }),
    refreshImplementingAgency: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.implementingAgency),
      invalidatesTags: ['ImplementingAgency'],
    }),
    refreshLocation: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.location),
      invalidatesTags: ['Location'],
    }),
    refreshPriority: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.priority),
      invalidatesTags: ['Priority'],
    }),
    refreshProgramType: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.programType),
      invalidatesTags: ['ProgramType'],
    }),
    refreshRank: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.rank),
      invalidatesTags: ['Rank'],
    }),
    refreshTrainingType: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.trainingType),
      invalidatesTags: ['TrainingType'],
    }),
    refreshBranch: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.branch),
      invalidatesTags: ['Branch'],
    }),
    refreshDivision: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.division),
      invalidatesTags: ['Division'],
    }),
    refreshProduct: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.product),
      invalidatesTags: ['Product'],
    }),
    refreshTbm: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.tbm),
      invalidatesTags: ['TBM'],
    }),
    refreshBuildVersion: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.buildVersion),
      invalidatesTags: ['BuildVersion'],
    }),
    refreshChecklist: builder.mutation<void, void>({
      queryFn: makeRefreshFn(CACHE_KEYS.checklist),
      invalidatesTags: ['Checklist'],
    }),
  }),
})

export const {
  useGetAnnouncementsQuery,
  useGetCalendarQuery,
  useGetKnowledgeGraphsQuery,
  useGetQandAQuery,
  useGetTooltipsQuery,
  useGetReleaseNotesQuery,
  useGetSiteFeedbackQuery,
  useGetPOCQuery,
  useGetQuickLinksQuery,
  useGetCountryQuery,
  useGetExaQuery,
  useGetFacilityCategoryQuery,
  useGetImplementingAgencyQuery,
  useGetLocationQuery,
  useGetPriorityQuery,
  useGetProgramTypeQuery,
  useGetRankQuery,
  useGetTrainingTypeQuery,
  useGetBranchQuery,
  useGetDivisionQuery,
  useGetProductQuery,
  useGetTbmQuery,
  useGetBuildVersionQuery,
  useGetChecklistQuery,
  useRefreshAnnouncementsMutation,
  useRefreshCalendarMutation,
  useRefreshKnowledgeGraphsMutation,
  useRefreshQandAMutation,
  useRefreshTooltipsMutation,
  useRefreshReleaseNotesMutation,
  useRefreshSiteFeedbackMutation,
  useRefreshPOCMutation,
  useRefreshQuickLinksMutation,
  useRefreshCountryMutation,
  useRefreshExaMutation,
  useRefreshFacilityCategoryMutation,
  useRefreshImplementingAgencyMutation,
  useRefreshLocationMutation,
  useRefreshPriorityMutation,
  useRefreshProgramTypeMutation,
  useRefreshRankMutation,
  useRefreshTrainingTypeMutation,
  useRefreshBranchMutation,
  useRefreshDivisionMutation,
  useRefreshProductMutation,
  useRefreshTbmMutation,
  useRefreshBuildVersionMutation,
  useRefreshChecklistMutation,
} = settingsApi
